import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';
import {
  createNeoAttackState,
  hasActiveNeoAttacksForApplication,
  normalizePersistedNeoAttackState,
  reconcileNeoAttacks,
  shouldPersistNeoAttackState,
  type BackendRunningNeoTrace,
  type NeoAttackState,
} from '../utils/neoAttackReconciliation';
import {
  isActiveNeoAttackStoreStatus,
  type NeoAttackStoreStatus,
} from '../utils/neoAttackStatus';

type BulkNeoAttackResult = {
  signalId: string;
  success: boolean;
  threadId: string | null;
  executionId: string | null;
  queued?: boolean;
  alreadyRunning?: boolean;
};

type BulkNeoAttackSummary = {
  succeeded: number;
  failed: number;
  queued: number;
};

type ExecutionsStore = {
  runningSignalIds: Set<string>;
  addRunningExecution: (signalId: string) => void;
  removeRunningExecution: (signalId: string) => void;
  clearRunningExecutions: () => void;
  isRunning: (signalId: string) => boolean;
  neoAttacks: Map<string, NeoAttackState>;
  startNeoAttack: (params: { signalId: string; applicationId: string }) => void;
  addNeoAttack: (params: {
    signalId: string;
    threadId: string;
    executionId: string | null;
    applicationId: string;
  }) => void;
  updateNeoAttackStatus: (params: {
    signalId: string;
    status: NeoAttackStoreStatus;
    threadId?: string;
  }) => void;
  startNeoAttacks: (params: {
    signalIds: string[];
    applicationId: string;
  }) => void;
  failNeoAttacks: (signalIds: string[]) => void;
  applyBulkNeoAttackResults: (params: {
    applicationId: string;
    results: BulkNeoAttackResult[];
  }) => BulkNeoAttackSummary;
  removeNeoAttack: (signalId: string) => void;
  removeNeoAttackByThreadId: (threadId: string) => void;
  reconcileNeoAttacksWithBackend: (params: {
    applicationId: string;
    runningTraces: BackendRunningNeoTrace[];
    serialQueue?: {
      signalIds: readonly string[];
      activeSignalId: string | null;
    };
  }) => void;
  hasActiveNeoAttacksForApplication: (applicationId: string) => boolean;
  getNeoAttackState: (signalId: string) => NeoAttackState | undefined;
  isNeoAttackRunning: (signalId: string) => boolean;
  getNeoAttackByThreadId: (threadId: string) =>
    | {
        signalId: string;
        state: NeoAttackState;
      }
    | undefined;
};

type PersistedExecutionsStore = {
  neoAttacks: Array<[string, Partial<NeoAttackState>]>;
};

export const useExecutionsStore = create<ExecutionsStore>()(
  persist(
    (set, get) => ({
      runningSignalIds: new Set(),

      addRunningExecution: (signalId) =>
        set((state) => {
          const newSet = new Set(state.runningSignalIds);

          newSet.add(signalId);

          return { runningSignalIds: newSet };
        }),

      removeRunningExecution: (signalId) =>
        set((state) => {
          const newSet = new Set(state.runningSignalIds);

          newSet.delete(signalId);

          return { runningSignalIds: newSet };
        }),

      clearRunningExecutions: () => set({ runningSignalIds: new Set() }),

      isRunning: (signalId) => get().runningSignalIds.has(signalId),

      neoAttacks: new Map(),

      startNeoAttack: ({ signalId, applicationId }) =>
        set((state) => {
          const newMap = new Map(state.neoAttacks);

          const existing = newMap.get(signalId);

          if (existing && isActiveNeoAttackStoreStatus(existing.status)) {
            return state;
          }

          newMap.set(
            signalId,
            createNeoAttackState({
              threadId: '',
              executionId: null,
              status: 'starting',
              applicationId,
            }),
          );

          return { neoAttacks: newMap };
        }),

      addNeoAttack: ({ signalId, threadId, executionId, applicationId }) =>
        set((state) => {
          const existing = state.neoAttacks.get(signalId);
          const resolvedApplicationId =
            applicationId || existing?.applicationId;

          if (!resolvedApplicationId) {
            return state;
          }

          const newMap = new Map(state.neoAttacks);

          newMap.set(
            signalId,
            createNeoAttackState({
              threadId,
              executionId,
              status: 'running',
              applicationId: resolvedApplicationId,
              startedAt: existing?.startedAt,
            }),
          );

          return { neoAttacks: newMap };
        }),

      updateNeoAttackStatus: ({ signalId, status, threadId }) =>
        set((state) => {
          const existing = state.neoAttacks.get(signalId);

          if (!existing) return state;
          const newMap = new Map(state.neoAttacks);

          newMap.set(signalId, {
            ...existing,
            status,
            threadId:
              threadId && threadId.length > 0 ? threadId : existing.threadId,
            updatedAt: new Date().toISOString(),
          });

          return { neoAttacks: newMap };
        }),

      startNeoAttacks: ({ signalIds, applicationId }) =>
        set((state) => {
          const newMap = new Map(state.neoAttacks);

          signalIds.forEach((signalId) => {
            const existing = newMap.get(signalId);

            if (existing && isActiveNeoAttackStoreStatus(existing.status)) {
              return;
            }

            newMap.set(
              signalId,
              createNeoAttackState({
                threadId: '',
                executionId: null,
                status: 'starting',
                applicationId,
              }),
            );
          });

          return { neoAttacks: newMap };
        }),

      failNeoAttacks: (signalIds) =>
        set((state) => {
          const newMap = new Map(state.neoAttacks);
          const updatedAt = new Date().toISOString();

          signalIds.forEach((signalId) => {
            const existing = newMap.get(signalId);

            if (existing?.status === 'starting') {
              newMap.set(signalId, {
                ...existing,
                status: 'failed',
                updatedAt,
              });
            }
          });

          return { neoAttacks: newMap };
        }),

      applyBulkNeoAttackResults: ({ applicationId, results }) => {
        const succeeded = results.filter(
          (result) => result.success && Boolean(result.threadId),
        ).length;
        const queued = results.filter((result) => result.queued).length;
        const alreadyRunning = results.filter(
          (result) => result.alreadyRunning,
        ).length;

        set((state) => {
          const newMap = new Map(state.neoAttacks);

          results.forEach((result) => {
            if (result.alreadyRunning) {
              return;
            }

            if (result.queued) {
              const existing = newMap.get(result.signalId);

              newMap.set(
                result.signalId,
                createNeoAttackState({
                  threadId: '',
                  executionId: null,
                  status: 'queued',
                  applicationId,
                  startedAt: existing?.startedAt,
                }),
              );

              return;
            }

            if (result.success && result.threadId) {
              const existing = newMap.get(result.signalId);

              newMap.set(
                result.signalId,
                createNeoAttackState({
                  threadId: result.threadId,
                  executionId: result.executionId,
                  status: 'running',
                  applicationId,
                  startedAt: existing?.startedAt,
                }),
              );

              return;
            }

            const existing = newMap.get(result.signalId);

            if (existing) {
              newMap.set(result.signalId, {
                ...existing,
                status: 'failed',
                updatedAt: new Date().toISOString(),
              });
            }
          });

          return { neoAttacks: newMap };
        });

        return {
          succeeded,
          failed: results.length - succeeded - queued - alreadyRunning,
          queued,
        };
      },

      removeNeoAttack: (signalId) =>
        set((state) => {
          const newMap = new Map(state.neoAttacks);

          newMap.delete(signalId);

          return { neoAttacks: newMap };
        }),

      removeNeoAttackByThreadId: (threadId) =>
        set((state) => {
          const newMap = new Map(state.neoAttacks);

          for (const [signalId, neoAttackState] of newMap.entries()) {
            if (neoAttackState.threadId === threadId) {
              newMap.delete(signalId);
              break;
            }
          }

          return { neoAttacks: newMap };
        }),

      reconcileNeoAttacksWithBackend: ({
        applicationId,
        runningTraces,
        serialQueue,
      }) =>
        set((state) => {
          const { nextEntries } = reconcileNeoAttacks({
            applicationId,
            cachedEntries: state.neoAttacks,
            backendRunningTraces: runningTraces,
            nowMs: Date.now(),
            serialQueue: serialQueue
              ? {
                  signalIds: new Set(serialQueue.signalIds),
                  activeSignalId: serialQueue.activeSignalId,
                }
              : undefined,
          });

          return { neoAttacks: nextEntries };
        }),

      hasActiveNeoAttacksForApplication: (applicationId) =>
        hasActiveNeoAttacksForApplication({
          cachedEntries: get().neoAttacks,
          applicationId,
        }),

      getNeoAttackState: (signalId) => get().neoAttacks.get(signalId),

      isNeoAttackRunning: (signalId) => {
        const state = get().neoAttacks.get(signalId);

        if (!state) {
          return false;
        }

        return isActiveNeoAttackStoreStatus(state.status);
      },

      getNeoAttackByThreadId: (threadId) => {
        const attacks = get().neoAttacks;

        for (const [signalId, state] of attacks.entries()) {
          if (state.threadId === threadId) {
            return { signalId, state };
          }
        }

        return undefined;
      },
    }),
    {
      name: 'terra-proxy-executions-store',
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: (state): PersistedExecutionsStore => {
        const nowMs = Date.now();

        return {
          neoAttacks: Array.from(state.neoAttacks.entries()).filter(
            ([, neoAttackState]) =>
              shouldPersistNeoAttackState({ state: neoAttackState, nowMs }),
          ),
        };
      },
      merge: (persistedState, currentState) => {
        const persisted = persistedState as
          | PersistedExecutionsStore
          | undefined;
        const nowMs = Date.now();
        const fallbackTimestamp = new Date(nowMs).toISOString();
        const normalizedEntries = (persisted?.neoAttacks ?? [])
          .map(([signalId, state]) => {
            const normalized = normalizePersistedNeoAttackState(
              state,
              fallbackTimestamp,
            );

            return [signalId, normalized] as [string, NeoAttackState];
          })
          .filter(([, state]) => shouldPersistNeoAttackState({ state, nowMs }));

        return {
          ...currentState,
          neoAttacks: new Map(normalizedEntries),
        };
      },
    },
  ),
);

export type { NeoAttackState, NeoAttackStoreStatus };
